import { ApplicationConfig, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { RestAuthInterceptor } from '@core/interceptors/rest-auth';
import { routes } from './app.routes';
import { createClient, Transport } from '@connectrpc/connect';
import { createGrpcWebTransport } from '@connectrpc/connect-web';
import { AuthData } from '@gen/src/protos/auth/v1/auth_pb';
import { UserData } from '@gen/src/protos/users/users_pb';
import { GrpcAuthInterceptor } from '@core/interceptors/grpc-auth';
import { environment } from '@env/environment';
import { NotificationService } from '@core/services/notification';
import { GrpcGlobalErrorInterceptor } from '@core/interceptors/grpc-global-error';
import { CatalogData } from '@gen/src/protos/catalogs/catalog_pb';
import { PlatformIdInterceptor } from '@core/interceptors/platform-id';
import { MFAInterceptor } from '@core/interceptors/mfa';
import {
  AUTH_CLIENT,
  CATALOG_CLIENT,
  GRPC_CATALOG_TRANSPORT,
  GRPC_TRANSPORT,
  USER_CLIENT,
} from '@tokens/injection-tokens';

const aprendeSsoUrl = environment.datasourceUrls.aprendeSso;
const catalogsUrl = environment.datasourceUrls.catalogs;

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
    ),
    provideHttpClient(withFetch(), withInterceptors([RestAuthInterceptor])),

    // PROVIDER: transporte único
    {
      provide: GRPC_TRANSPORT,
      useFactory: (notificationService: NotificationService) => {
        return createGrpcWebTransport({
          baseUrl: aprendeSsoUrl,
          useBinaryFormat: true,
          fetch: (input: RequestInfo | URL, init?: RequestInit) => {
            return fetch(input, {
              ...(init ?? {}),
              credentials: 'include',
              duplex: 'half',
            } as RequestInit & { duplex: 'half' });
          },
          interceptors: [
            GrpcGlobalErrorInterceptor(notificationService),
            GrpcAuthInterceptor(),
            PlatformIdInterceptor,
            MFAInterceptor(),
          ],
        });
      },
      deps: [NotificationService],
    },
    {
      provide: GRPC_CATALOG_TRANSPORT,
      useFactory: (notificationService: NotificationService) => {
        return createGrpcWebTransport({
          baseUrl: catalogsUrl,
          useBinaryFormat: true,
          fetch: (input: RequestInfo | URL, init?: RequestInit) => {
            return fetch(input, {
              ...(init ?? {}),
              credentials: 'include',
              duplex: 'half',
            } as RequestInit & { duplex: 'half' });
          },
          interceptors: [GrpcGlobalErrorInterceptor(notificationService), GrpcAuthInterceptor()],
        });
      },
      deps: [NotificationService],
    },
    // AUTH client
    {
      provide: AUTH_CLIENT,
      useFactory: (transport: Transport) => {
        return createClient(AuthData, transport);
      },
      deps: [GRPC_TRANSPORT],
    },

    // USER client
    {
      provide: USER_CLIENT,
      useFactory: (transport: Transport) => {
        return createClient(UserData, transport);
      },
      deps: [GRPC_TRANSPORT],
    },
    {
      provide: CATALOG_CLIENT,
      useFactory: (transport: Transport) => {
        return createClient(CatalogData, transport);
      },
      deps: [GRPC_CATALOG_TRANSPORT],
    },
  ],
};
